import { useEffect, useRef, useState, useMemo } from 'react';
import { Listbox } from '@headlessui/react';
import moment from 'moment';
import {
	CheckIcon,
	ChevronUpDownIcon,
	PlusIcon,
	TrashIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
} from '@heroicons/react/20/solid';

import {
	createPMSchedule,
	updatePMSchedule,
	deletePMSchedule,
} from '../api/pmSchedules';
import { getModuleCategoriesByModuleKey, getModules } from '../api/modules';
import { getAssets } from '../api/assets';
import { getContracts } from '../api/contracts';
import { getApprovedSops } from '../api/sops';

import {
	StackableModal,
	DatePicker,
	AssetSelectorBeta,
} from '../components/nextgen';
import { classNames } from '../components/helpers/functions';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'editPMSchedule';

const FREQUENCY_OPTIONS = [
	{ key: 'daily', name: 'Daily' },
	{ key: 'weekly', name: 'Weekly' },
	{ key: 'biweekly', name: 'Bi-weekly' },
	{ key: 'monthly', name: 'Monthly' },
	{ key: 'quarterly', name: 'Quarterly' },
	{ key: 'biyearly', name: 'Bi-yearly' },
	{ key: 'yearly', name: 'Yearly' },
	{ key: 'custom', name: 'Custom Dates' },
];

const STEPS = [
	{ id: 'details', name: 'Details' },
	{ id: 'assets', name: 'Assets' },
];

const EditPMScheduleModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const { setOpen: openAddCategoryModal } = useModal('addCategoryModal');
	const nameInputRef = useRef(null);

	const [form, setForm] = useState({
		name: '',
		description: '',
		categoryId: null,
		occurrences: 'yearly',
		customDates: [],
		startDate: '',
		generateDaysBefore: 30,
		assets: [],
		contractId: null,
		sopId: null,
		serviceReportRequired: true,
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);

	const isEditing = !!open?.schedule?.id;

	// Data fetching
	const { value: modules } = useAsync(getModules, {
		defaultPayload: {},
	});
	const { value: categories, execute: refreshCategories } = useAsync(
		getModuleCategoriesByModuleKey,
		{
			defaultPayload: { key: 'default:pm_schedule' },
		},
	);
	const { value: assets } = useAsync(getAssets, {
		defaultPayload: { pageSize: null },
	});
	const { value: contracts } = useAsync(getContracts, {
		defaultPayload: { pageSize: null },
	});
	const { value: sops } = useAsync(getApprovedSops, {
		defaultPayload: {},
	});

	// Get the PM Schedule module ID
	const pmScheduleModule = modules?.rows?.find(
		(m) => m.key === 'default:pm_schedule',
	);

	// Process assets data for selector
	const processedAssets = useMemo(() => {
		if (!assets?.rows) return [];
		return assets.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assets]);

	// Dynamic steps - skip assets page when contract is selected
	const steps = useMemo(() => {
		if (form.contractId) {
			return [{ id: 'details', name: 'Details' }];
		}
		return STEPS;
	}, [form.contractId]);

	useEffect(() => {
		if (open?.schedule) {
			const schedule = open.schedule;
			setForm({
				name: schedule.name || '',
				description: schedule.description || '',
				categoryId: schedule.categoryId || null,
				occurrences: schedule.occurrences || 'yearly',
				customDates: schedule.customDates || [],
				startDate: schedule.startDate ? schedule.startDate.split('T')[0] : '',
				generateDaysBefore: schedule.generateDaysBefore || 30,
				assets: schedule.directAssets?.map((a) => a.id) || [],
				contractId: schedule.contractId || null,
				sopId: schedule.sopId || null,
				serviceReportRequired: schedule.serviceReportRequired !== false,
			});
		} else if (open) {
			setForm({
				name: '',
				description: '',
				categoryId: null,
				occurrences: 'yearly',
				customDates: [],
				startDate: moment().format('YYYY-MM-DD'),
				generateDaysBefore: 30,
				assets: open.assetId ? [open.assetId] : [],
				contractId: open.contractId || null,
				sopId: null,
				serviceReportRequired: true,
			});
		}
	}, [open]);

	const handleClose = () => {
		setForm({
			name: '',
			description: '',
			categoryId: null,
			occurrences: 'yearly',
			customDates: [],
			startDate: moment().format('YYYY-MM-DD'),
			generateDaysBefore: 30,
			assets: [],
			contractId: null,
			sopId: null,
			serviceReportRequired: true,
		});
		setShowDeleteConfirm(false);
		setCurrentStep(0);
		setClose();
		if (typeof open?.onClose === 'function') {
			open.onClose();
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.name.trim()) return;

		// Capture callback before async operation to avoid losing it during re-renders
		const onSuccessCallback = open?.onSuccess;

		setIsSubmitting(true);

		try {
			const payload = {
				name: form.name,
				description: form.description,
				categoryId: form.categoryId,
				occurrences: form.occurrences,
				customDates: form.occurrences === 'custom' ? form.customDates : null,
				startDate: form.startDate || null,
				generateDaysBefore: parseInt(form.generateDaysBefore) || 30,
				assets: form.assets,
				contractId: form.contractId,
				sopId: form.sopId,
				serviceReportRequired: form.serviceReportRequired,
			};

			if (isEditing) {
				await updatePMSchedule({ id: open.schedule.id, ...payload });
			} else {
				await createPMSchedule(payload);
			}

			if (typeof onSuccessCallback === 'function') {
				onSuccessCallback();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to save PM Schedule:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		if (!isEditing) return;

		// Capture callbacks before async operation to avoid losing them during re-renders
		const onDeletedCallback = open?.onDeleted;
		const onSuccessCallback = open?.onSuccess;
		const scheduleId = open.schedule.id;

		setIsSubmitting(true);
		try {
			await deletePMSchedule({ id: scheduleId });
			if (typeof onDeletedCallback === 'function') {
				onDeletedCallback();
			} else if (typeof onSuccessCallback === 'function') {
				onSuccessCallback();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to delete PM Schedule:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Asset management
	const toggleAsset = (assetId) => {
		if (form.assets.includes(assetId)) {
			setForm({ ...form, assets: form.assets.filter((id) => id !== assetId) });
		} else {
			setForm({ ...form, assets: [...form.assets, assetId] });
		}
	};

	const removeAsset = (assetId) => {
		setForm({ ...form, assets: form.assets.filter((id) => id !== assetId) });
	};

	// Custom date management
	const addCustomDate = () => {
		setForm({ ...form, customDates: [...form.customDates, ''] });
	};

	const updateCustomDate = (index, value) => {
		const newDates = [...form.customDates];
		newDates[index] = value;
		setForm({ ...form, customDates: newDates });
	};

	const removeCustomDate = (index) => {
		setForm({
			...form,
			customDates: form.customDates.filter((_, i) => i !== index),
		});
	};

	const handleAddCategory = () => {
		if (!pmScheduleModule?.id) {
			console.error('PM Schedule module not found');
			return;
		}
		openAddCategoryModal({
			moduleId: pmScheduleModule.id,
			onSubmit: (newCategory) => {
				refreshCategories({ key: 'default:pm_schedule' });
				// Auto-select the newly created category
				if (newCategory?.value) {
					setForm((prev) => ({ ...prev, categoryId: newCategory.value }));
				}
			},
		});
	};

	// Check if current step is valid for navigation
	const canProceed = () => {
		if (currentStep === 0) {
			return form.name.trim().length > 0;
		}
		return true;
	};

	const goToNextStep = (e) => {
		e?.preventDefault();
		e?.stopPropagation();
		if (currentStep < steps.length - 1 && canProceed()) {
			setCurrentStep(currentStep + 1);
		}
	};

	const goToPreviousStep = (e) => {
		e?.preventDefault();
		e?.stopPropagation();
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title={isEditing ? 'Edit PM Schedule' : 'Create PM Schedule'}
			size="lg"
			initialFocusRef={nameInputRef}
		>
			<div className="">
				<form onSubmit={handleSubmit} className="space-y-4 ">
					{/* Step 1: Details */}
					{currentStep === 0 && (
						<div className="space-y-4">
							{/* Name */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Name <span className="text-red-500">*</span>
								</label>
								<input
									ref={nameInputRef}
									type="text"
									value={form.name}
									onChange={(e) => setForm({ ...form, name: e.target.value })}
									placeholder="Enter PM schedule name"
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
								/>
							</div>

							{/* Description */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Description
								</label>
								<textarea
									value={form.description}
									onChange={(e) =>
										setForm({ ...form, description: e.target.value })
									}
									placeholder="Enter description..."
									rows={3}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
								/>
							</div>

							{/* Category with + button */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Category
								</label>
								<div className="flex gap-2">
									<Listbox
										value={form.categoryId}
										onChange={(val) => setForm({ ...form, categoryId: val })}
									>
										<div className="relative flex-1">
											<Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm">
												<span>
													{form.categoryId
														? categories?.find((c) => c.id === form.categoryId)
																?.name
														: 'Select category'}
												</span>
												<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
													<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
												</span>
											</Listbox.Button>
											<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
												<Listbox.Option
													value={null}
													className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
												>
													No category
												</Listbox.Option>
												{[...(categories || [])]
													.sort((a, b) =>
														(a.name || '').localeCompare(
															b.name || '',
															undefined,
															{
																numeric: true,
																sensitivity: 'base',
															},
														),
													)
													.map((category) => (
														<Listbox.Option
															key={category.id}
															value={category.id}
															className={({ active }) =>
																classNames(
																	active ? 'bg-indigo-50' : '',
																	'relative cursor-pointer select-none py-2 pl-3 pr-9',
																)
															}
														>
															{({ selected }) => (
																<>
																	<span>{category.name}</span>
																	{selected && (
																		<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																			<CheckIcon className="h-5 w-5" />
																		</span>
																	)}
																</>
															)}
														</Listbox.Option>
													))}
											</Listbox.Options>
										</div>
									</Listbox>
									<button
										type="button"
										onClick={handleAddCategory}
										className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-600"
										title="Add category"
									>
										<PlusIcon className="h-5 w-5" />
									</button>
								</div>
							</div>

							{/* Contract */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Contract (Optional)
								</label>
								<Listbox
									value={form.contractId}
									onChange={(contractId) => {
										const selectedContract = contracts?.rows?.find(
											(c) => c.id === contractId,
										);

										// Prefill start date from contract if not already set
										const startDate =
											!form.startDate && selectedContract?.startDate
												? selectedContract.startDate.split('T')[0]
												: form.startDate;

										// Clear assets when contract is selected (assets are managed via contract)
										setForm({
											...form,
											contractId,
											assets: contractId ? [] : form.assets,
											startDate,
										});
									}}
								>
									<div className="relative">
										<Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm">
											<span>
												{form.contractId
													? contracts?.rows?.find(
															(c) => c.id === form.contractId,
														)?.name
													: 'Select contract'}
											</span>
											<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
												<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
											</span>
										</Listbox.Button>
										<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
											<Listbox.Option
												value={null}
												className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
											>
												No contract
											</Listbox.Option>
											{contracts?.rows?.map((contract) => (
												<Listbox.Option
													key={contract.id}
													value={contract.id}
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<span>{contract.name}</span>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											))}
										</Listbox.Options>
									</div>
								</Listbox>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Frequency
								</label>
								<Listbox
									value={form.occurrences}
									onChange={(val) => setForm({ ...form, occurrences: val })}
								>
									<div className="relative">
										<Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm">
											<span>
												{FREQUENCY_OPTIONS.find(
													(f) => f.key === form.occurrences,
												)?.name || 'Yearly'}
											</span>
											<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
												<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
											</span>
										</Listbox.Button>
										<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
											{FREQUENCY_OPTIONS.map((option) => (
												<Listbox.Option
													key={option.key}
													value={option.key}
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<span>{option.name}</span>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											))}
										</Listbox.Options>
									</div>
								</Listbox>
							</div>

							{/* Custom Dates (only shown when frequency is custom) */}
							{form.occurrences === 'custom' && (
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Custom Dates
									</label>
									<div className="space-y-2">
										{form.customDates.map((date, index) => (
											<div key={index} className="flex items-center gap-2">
												<input
													type="date"
													value={date}
													onChange={(e) =>
														updateCustomDate(index, e.target.value)
													}
													className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
												/>
												<button
													type="button"
													onClick={() => removeCustomDate(index)}
													className="p-2 text-gray-400 hover:text-red-600"
												>
													<TrashIcon className="w-4 h-4" />
												</button>
											</div>
										))}
										<button
											type="button"
											onClick={addCustomDate}
											className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700"
										>
											<PlusIcon className="w-4 h-4" />
											Add Date
										</button>
									</div>
								</div>
							)}

							{/* Start Date */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Start Date
								</label>
								<DatePicker
									value={form.startDate}
									onChange={(date) => setForm({ ...form, startDate: date })}
									placeholder="Select start date"
									clearable={false}
								/>
							</div>

							{/* Lead Time */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Lead Time (days)
								</label>
								<input
									type="number"
									min="0"
									value={form.generateDaysBefore}
									onChange={(e) =>
										setForm({ ...form, generateDaysBefore: e.target.value })
									}
									className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
								/>
								<p className="text-xs text-gray-500 mt-1">
									Days before due date to generate work order
								</p>
							</div>

							{/* SOP Template */}
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									SOP Template (Optional)
								</label>
								<Listbox
									value={form.sopId}
									onChange={(val) => setForm({ ...form, sopId: val })}
								>
									<div className="relative">
										<Listbox.Button className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm">
											<span>
												{form.sopId
													? sops?.find((s) => s.id === form.sopId)?.name
													: 'Select SOP template'}
											</span>
											<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
												<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
											</span>
										</Listbox.Button>
										<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
											<Listbox.Option
												value={null}
												className="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-500"
											>
												No SOP template
											</Listbox.Option>
											{sops?.map((sop) => (
												<Listbox.Option
													key={sop.id}
													value={sop.id}
													className={({ active }) =>
														classNames(
															active ? 'bg-indigo-50' : '',
															'relative cursor-pointer select-none py-2 pl-3 pr-9',
														)
													}
												>
													{({ selected }) => (
														<>
															<div>
																<span className="block truncate">
																	{sop.name}
																</span>
																{sop.steps?.length > 0 && (
																	<span className="text-xs text-gray-500">
																		{sop.steps.length} step
																		{sop.steps.length !== 1 ? 's' : ''}
																	</span>
																)}
															</div>
															{selected && (
																<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																	<CheckIcon className="h-5 w-5" />
																</span>
															)}
														</>
													)}
												</Listbox.Option>
											))}
										</Listbox.Options>
									</div>
								</Listbox>
							</div>

							{/* Service Report Required */}
							<div className="flex items-center gap-3 pt-2">
								<input
									type="checkbox"
									id="serviceReportRequired"
									checked={form.serviceReportRequired}
									onChange={(e) =>
										setForm({
											...form,
											serviceReportRequired: e.target.checked,
										})
									}
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
								/>
								<div>
									<label
										htmlFor="serviceReportRequired"
										className="text-sm font-medium text-gray-700"
									>
										Service Report Required
									</label>
									<p className="text-xs text-gray-500">
										Require a PDF service report to be uploaded before
										completing work orders generated from this schedule
									</p>
								</div>
							</div>
						</div>
					)}

					{/* Step 2: Assets */}
					{currentStep === 1 && (
						<div className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Assets
								</label>
								<p className="text-xs text-gray-500 mb-3">
									Select assets to include in this PM schedule. Work orders will
									be generated for these assets.
								</p>

								<AssetSelectorBeta
									value={form.assets}
									onChange={(assetIds) => {
										setForm((prev) => ({
											...prev,
											assets: assetIds,
										}));
									}}
									assets={processedAssets}
									placeholder="Select assets by name, serial number, location, etc…"
									multiple={true}
									allowGroups={false}
									className="mt-3"
								/>
							</div>
						</div>
					)}

					{/* Delete Confirmation */}
					{showDeleteConfirm && (
						<div className="bg-red-50 border border-red-200 rounded-lg p-4">
							<p className="text-sm text-red-700 mb-3">
								Are you sure you want to delete this PM schedule? This action
								cannot be undone.
							</p>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={handleDelete}
									disabled={isSubmitting}
									className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
								>
									{isSubmitting ? 'Deleting...' : 'Delete'}
								</button>
								<button
									type="button"
									onClick={() => setShowDeleteConfirm(false)}
									className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
								>
									Cancel
								</button>
							</div>
						</div>
					)}

					{/* Actions */}
					<div className="flex justify-between gap-3 pt-4  border-gray-200">
						<div className="flex items-center gap-3">
							{isEditing && !showDeleteConfirm && (
								<button
									type="button"
									onClick={() => setShowDeleteConfirm(true)}
									className="text-sm text-red-600 hover:text-red-700"
								>
									Delete
								</button>
							)}
						</div>
						<div className="flex gap-3">
							{currentStep > 0 && (
								<button
									type="button"
									onClick={goToPreviousStep}
									className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
								>
									<ChevronLeftIcon className="h-4 w-4 mr-1" />
									Back
								</button>
							)}
							{currentStep === 0 && (
								<button
									type="button"
									onClick={handleClose}
									className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
								>
									Cancel
								</button>
							)}
							{currentStep < steps.length - 1 ? (
								<button
									type="button"
									onClick={goToNextStep}
									disabled={!canProceed()}
									className={classNames(
										'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
										!canProceed()
											? 'bg-gray-400 cursor-not-allowed'
											: 'bg-indigo-600 hover:bg-indigo-700',
									)}
								>
									Next
									<ChevronRightIcon className="h-4 w-4 ml-1" />
								</button>
							) : (
								<button
									type="submit"
									disabled={!form.name.trim() || isSubmitting}
									className={classNames(
										'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
										!form.name.trim() || isSubmitting
											? 'bg-gray-400 cursor-not-allowed'
											: 'bg-indigo-600 hover:bg-indigo-700',
									)}
								>
									{isSubmitting
										? 'Saving...'
										: isEditing
											? 'Save Changes'
											: 'Create PM Schedule'}
								</button>
							)}
						</div>
					</div>
				</form>
			</div>
		</StackableModal>
	);
};

export default EditPMScheduleModal;
